import { dispatchAction } from '@ardoq/rxbeach';
import { viewpointBuilderTracking } from '../viewpointBuilderTracking';
import * as editTraversalActions from './editTraversalActions';
import type {
  ChangeWildcardTraversalPayload,
  SetSectionFilterPayload,
  UpdateTraversalStartContextPayload,
  SetTripleSortOrderPayload,
} from './editTraversalActions';
import {
  DirectedTriple,
  ReferenceDirection,
  TraversalPathMatchingType,
  ViewpointBuilderFilters,
  StartSetExpansion,
} from '@ardoq/api-types';
import { APICountInstancesResponse } from '@ardoq/api-types';

export const getEditTraversalCommands = (namespace: string) => ({
  setStartType: (startType: string) =>
    dispatchAction(
      editTraversalActions.setStartTypeAndTraversalStartSetOrCount({
        startType,
      }),
      namespace
    ),

  setStartTypeAndTraversalStartSetOrCount: (
    startType: string,
    traversalStartSet: string[],
    traversalStartQueryResultCount?: number
  ) =>
    dispatchAction(
      editTraversalActions.setStartTypeAndTraversalStartSetOrCount({
        startType,
        traversalStartSet,
        traversalStartQueryResultCount,
      }),
      namespace
    ),

  setTraversals: (
    paths: DirectedTriple[][],
    filters: ViewpointBuilderFilters | undefined
  ) =>
    dispatchAction(
      editTraversalActions.setTraversals({ paths, filters: filters ?? null }),
      namespace
    ),

  changeWildcardTraversal: (paths: ChangeWildcardTraversalPayload['paths']) =>
    dispatchAction(
      editTraversalActions.changeWildcardTraversal({ paths }),
      namespace
    ),

  setTripleSortOrder: (payload: SetTripleSortOrderPayload) =>
    dispatchAction(editTraversalActions.setTripleSortOrder(payload), namespace),

  toggleIsSelectedGraphNode: (
    graphNodeId: string,
    isForceSelected?: boolean
  ) => {
    viewpointBuilderTracking.trackComponentTypeNodeSelectedInGraph(
      'SUBGRAPH_TAB'
    );
    dispatchAction(
      editTraversalActions.toggleIsSelectedGraphNode({
        graphNodeId,
        isForceSelected,
      }),
      namespace
    );
  },

  toggleTripleOption: (
    tripleId: string,
    direction: ReferenceDirection,
    namedDirectedTriple: DirectedTriple,
    isSelected: boolean
  ) =>
    dispatchAction(
      editTraversalActions.toggleTripleOption({
        tripleId,
        direction,
        namedDirectedTriple,
        isSelected,
      }),
      namespace
    ),

  removeNodeFromGraph: (nodeId: string) =>
    dispatchAction(editTraversalActions.removeNodeFromGraph(nodeId), namespace),

  responseInstanceCounts: (payload: APICountInstancesResponse) =>
    dispatchAction(
      editTraversalActions.responseInstanceCounts(payload),
      namespace
    ),

  setSectionFilter: (payload: SetSectionFilterPayload) =>
    dispatchAction(editTraversalActions.setSectionFilter(payload), namespace),

  toggleShowOnlyOptionsWithInstanceCounts: () =>
    dispatchAction(
      editTraversalActions.toggleShowOnlyOptionsWithInstanceCounts(),
      namespace
    ),

  skipClickOtherComponentTypeNodesHints: () => {
    dispatchAction(
      editTraversalActions.dontShowAgainClickOtherComponentTypeNodesHints(),
      namespace
    );
  },

  updateStartContext: (payload: UpdateTraversalStartContextPayload) =>
    dispatchAction(
      editTraversalActions.updateTraversalStartContext(payload),
      namespace
    ),

  setPathMatchingType: (pathMatching: TraversalPathMatchingType) =>
    dispatchAction(
      editTraversalActions.setPathMatchingType(pathMatching),
      namespace
    ),

  expandDefaultTraversalRequested: () => {
    dispatchAction(
      editTraversalActions.expandDefaultTraversalRequested(),
      namespace
    );
  },

  setStartSetExpansion: (startSetExpansion: StartSetExpansion | null) =>
    dispatchAction(
      editTraversalActions.setStartSetExpansion(startSetExpansion),
      namespace
    ),
});

export type EditTraversalCommands = ReturnType<typeof getEditTraversalCommands>;
